import type React from 'react';
import { type ReactNode, useMemo } from 'react';

import { mapImagesToFilmstripBadges } from '../../helpers/filmstripBadges';
import { shouldLoadFilmstripJpeg } from '../../helpers/filmstripJpegWindow';
import type { Image } from '../../types';
import type { FilmstripLabels, VariantBehaviour } from '../../variants';

import DayMarker from './DayMarker';
import Filmstrip from './Filmstrip';
import FilmstripCard from './FilmstripCard';

export interface ViewerFilmstripProps {
    images: Image[];
    selectedIndex: number;
    currentImageId?: string;
    /** The current image's day, `YYYY-MM-DD` in the location's timezone. */
    currentDay?: string;
    daysWithImages: { day: string; count: number }[];
    behaviour: VariantBehaviour;
    liceRegion?: 'norway' | 'chile';
    onDayChange: (value: string) => void;
    onCardClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
 * The scrollable strip of thumbnails, grouped by day, and the day scrubber underneath it.
 *
 * Owns the badge counts and the bioscope JPEG window, both of which are derived from
 * `images` - the container used to hold the badges in state and recompute them from an
 * effect, which is derived data kept in state for no reason.
 */
export default function ViewerFilmstrip(props: ViewerFilmstripProps) {
    const {
        images,
        selectedIndex,
        currentImageId,
        currentDay,
        daysWithImages,
        behaviour,
        liceRegion = 'norway',
        onDayChange,
        onCardClick,
    } = props;

    const badges = useMemo(() => mapImagesToFilmstripBadges(images), [images]);

    const windowJpegs = behaviour.windowFilmstripJpegs;
    const labels: FilmstripLabels = behaviour.filmstripLabels;

    const items: ReactNode[] = [];
    badges.forEach((badge, index) => {
        const day = images[index]?.day;
        if (day && day !== images[index - 1]?.day) {
            items.push(<DayMarker key={`day-${day}-${index}`} day={day} />);
        }
        const inJpegWindow = !windowJpegs || shouldLoadFilmstripJpeg(index, selectedIndex);
        items.push(
            <FilmstripCard
                key={`${badge.imageId}${index}`}
                imagePath={inJpegWindow ? badge.path : undefined}
                lazyLoadImage={!windowJpegs}
                imageId={badge.imageId}
                currentVisibleImageId={currentImageId}
                onClickHandler={onCardClick}
                healing={badge.healing}
                active={badge.active}
                movable={badge.movable}
                skottelus={badge.skottelus}
                adult_female_lice={badge.adult_female_lice}
                chilean_female_lice={badge.chilean_female_lice}
                laksvelScores={badge.laksvelScores}
                showWoundLabel={labels === 'wound'}
                showLiceLabel={labels === 'lice' && liceRegion === 'norway'}
                showChileanLiceLabel={labels === 'lice' && liceRegion === 'chile'}
                showLaksvelLabel={labels === 'laksvel'}
            />
        );
    });

    return (
        <Filmstrip
            denseThumbnails={windowJpegs}
            days={daysWithImages}
            onDayChange={onDayChange}
            currentDay={currentDay}
            currentImageId={currentImageId}>
            {items}
        </Filmstrip>
    );
}
